/**
 * useBodyPartTranslations hook
 *
 * V2 Live Testing에서 body_select 필드의 body part 레이블 번역을 위해
 * V2 REST API로 translations 데이터를 로드하고 PaDataRepoState에 설정하는 hook
 *
 * 문제 배경:
 * - useTranslations hook은 PaDataRepoState.translations를 읽음
 * - patient survey flow에서는 GraphQL을 통해 translations가 로드됨
 * - V2 Live Testing은 admin 페이지이므로 translations가 비어있음
 * - 이 hook으로 V2 REST API를 통해 translations를 로드하고 PaDataRepoState에 설정
 */

import { useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { translationControllerTransForTrees } from '../../../../api/generated/translation/translation';
import { PaDataRepoState } from '../../../../../report/use-data';

interface UseBodyPartTranslationsProps {
  surveyId?: string | number;
  treeKeys: string[];
  locale: string;
  enabled?: boolean;
}

interface TranslationApiResponse {
  id?: number;
  key: string;
  value?: string;
  locale?: string;
  type?: string;
  dataType?: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data?: {
    translations?: TranslationApiResponse[];
  };
  timestamp: string;
}

export function useBodyPartTranslations({
  surveyId,
  treeKeys,
  locale,
  enabled = true,
}: UseBodyPartTranslationsProps) {
  const setDataState = useSetRecoilState(PaDataRepoState);
  const loadedRef = useRef<string | null>(null);

  useEffect(() => {
    // 조건 체크: surveyId, treeKeys, locale이 있어야 함
    if (!enabled || !surveyId || !treeKeys?.length || !locale) {
      return;
    }

    // 이미 같은 조건으로 로드했으면 스킵
    const cacheKey = `${surveyId}-${treeKeys.join(',')}-${locale}`;
    if (loadedRef.current === cacheKey) {
      return;
    }

    // Translations 로드
    async function loadTranslations() {
      try {
        const response = await translationControllerTransForTrees({
          surveyId: Number(surveyId),
          keys: treeKeys,
          locale,
          type: 'patient', // body part 번역은 patient 타입
        });

        // API 응답에서 translations 추출
        // 응답 구조: { success, message, data: { translations: [...] }, timestamp }
        const apiResponse = response as ApiResponse;
        const apiTranslations = apiResponse?.data?.translations || [];

        if (apiTranslations.length > 0) {
          // PaDataRepoState.translations 형식으로 변환
          const formattedTranslations = apiTranslations.map((t) => ({
            id: t.id?.toString() || null,
            key: t.key,
            value: t.value || null,
            locale: t.locale || null,
            type: null,
            dataType: null,
          }));

          // PaDataRepoState에 translations 설정
          setDataState((prev) => {
            // 기존 translations와 새로운 translations 병합
            // 같은 key + locale이면 새로운 값으로 덮어쓰기
            const existingTranslations = prev.translations || [];
            const newTranslationsMap = new Map<string, (typeof prev.translations)[number]>();

            // 기존 translations 추가
            existingTranslations.forEach((tr) => {
              const uniqueKey = `${tr.key}-${tr.locale}`;
              newTranslationsMap.set(uniqueKey, tr);
            });

            // 새로운 translations 추가 (덮어쓰기)
            formattedTranslations.forEach((tr) => {
              const uniqueKey = `${tr.key}-${tr.locale}`;
              newTranslationsMap.set(uniqueKey, tr as (typeof prev.translations)[number]);
            });

            const finalTranslations = Array.from(newTranslationsMap.values());

            return {
              ...prev,
              translations: finalTranslations,
            };
          });

          loadedRef.current = cacheKey;
        }
      } catch (error) {
        console.error('Failed to load body part translations:', error);
      }
    }

    loadTranslations();
  }, [surveyId, treeKeys, locale, enabled, setDataState]);
}
